import type { UserManager } from "oidc-client-ts";
import { asError, eventBus } from "@optimyl/events";

const RETURN_PATH_KEY = "preLoginUrl";
const LAST_SIGN_IN_AGAIN_KEY = "optimyl.auth.lastSignInAgainAt";
/** A second sign-in-again sooner than this after the last is a loop (the API refuses what a fresh sign-in gets), not a lapse. */
export const SIGN_IN_AGAIN_INTERVAL_MS = 30_000;

// Set once the tab is leaving for the identity server; a later request to leave is ignored. One per page: every app's
// createAuth shares it, so a sign-out from one blocks a sign-in from any other.
let isSigningOut = false;
let pendingSignIn: Promise<void> | null = null;

// A page restored from the back/forward cache is live again: whatever it was leaving for didn't happen.
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      isSigningOut = false;
      pendingSignIn = null;
    }
  });
}

export interface SignInOptions {
  /** Ask for the sign-in page even when the identity server would sign the browser straight back in. */
  forcePrompt?: boolean;
  /** The app path to come back to after signing in. Left as it is when not given. */
  returnTo?: string;
}

/**
 * Sends the browser to the Optimyl sign-in with the app's query parameters, in place of the page that asked: Back from the
 * sign-in leaves the app instead of landing on a page that sends the browser straight back. Starts once: while a sign-in
 * or a sign-out is already under way, it returns without starting another.
 */
export function startSignIn(
  userManager: UserManager,
  { forcePrompt = false, returnTo }: SignInOptions = {},
  extraQueryParams: Record<string, string> = {},
): Promise<void> {
  if (isSigningOut) return Promise.resolve();
  if (pendingSignIn) return pendingSignIn;

  if (returnTo) sessionStorage.setItem(RETURN_PATH_KEY, returnTo);
  pendingSignIn = userManager
    .signinRedirect({
      redirectMethod: "replace",
      extraQueryParams,
      ...(forcePrompt ? { prompt: "login" } : {}),
    })
    .catch((err: unknown) => {
      pendingSignIn = null;
      throw err;
    });
  return pendingSignIn;
}

/**
 * The session is gone (a 401, a refused refresh): signs in again and comes back to this page. Not awaited by callers:
 * the redirect settles only if the page is shown again, and a failure to start is reported on the event bus.
 *
 * Once per SIGN_IN_AGAIN_INTERVAL_MS: when a fresh sign-in comes straight back to another refusal (the API rejects the
 * token for a reason signing in can't fix), the tab stays and the refusal is reported, rather than bouncing between the
 * app and the identity server for ever.
 */
export function signInAgain(userManager: UserManager, context: string, extraQueryParams: Record<string, string> = {}): void {
  if (pendingSignIn || isSigningOut) return;
  const lastAt = Number(sessionStorage.getItem(LAST_SIGN_IN_AGAIN_KEY) ?? 0);
  if (Date.now() - lastAt < SIGN_IN_AGAIN_INTERVAL_MS) {
    eventBus.publish("error", {
      error: new Error("We couldn't sign you in again. If this keeps happening, ask your Optimyl contact."),
      context: `${context}.refusedAfterSignIn`,
    });
    return;
  }

  sessionStorage.setItem(LAST_SIGN_IN_AGAIN_KEY, String(Date.now()));
  startSignIn(userManager, { returnTo: currentPage() }, extraQueryParams).catch((err: unknown) => {
    // The tab never left, so this wasn't a round trip: the next refusal may try again.
    sessionStorage.removeItem(LAST_SIGN_IN_AGAIN_KEY);
    eventBus.publish("error", { error: asError(err), context });
  });
}

/**
 * Sends the browser to the identity server's sign-out with this tab's id token, which signs this browser out of every
 * Optimyl app. The tab's stored session is cleared first without raising `userUnloaded`, so the app never shows itself
 * signed out and starts a sign-in. Starts once; sign-ins asked for while leaving are ignored.
 *
 * `before` (releasing the employer app's form lock) runs after the tab is marked as signing out, so a 401 it gets can't
 * start a sign-in that lands after the sign-out. Its failure doesn't stop the sign-out. A sign-in already under way when
 * Sign out is clicked can't be called back: oidc-client-ts navigates once its state is stored.
 */
export async function startSignOut(
  userManager: UserManager | null,
  identityUrl: string,
  clientId: string,
  before?: () => Promise<void>,
): Promise<void> {
  if (isSigningOut) return;
  isSigningOut = true;

  if (before) {
    try {
      await before();
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "startSignOut.before" });
    }
  }

  const params = new URLSearchParams({ client_id: clientId });
  if (userManager) {
    try {
      const user = await userManager.getUser();
      if (user?.id_token) params.set("id_token_hint", user.id_token);
      await userManager.storeUser(null);
    } catch (err) {
      eventBus.publish("error", { error: asError(err), context: "startSignOut" });
    }
  }

  window.location.href = `${identityUrl}/connect/logout?${params}`;
}

/** Whether the tab is leaving for the identity server (a sign-in or a sign-out under way): nothing it shows will be seen. */
export function isRedirecting(): boolean {
  return isSigningOut || pendingSignIn !== null;
}

/** This tab's current page, as a path to come back to. */
export function currentPage(): string {
  return window.location.pathname + window.location.search;
}

/** The page remembered for after sign-in, or null when there is none or it isn't a path on this site. */
export function readReturnPath(): string | null {
  const path = sessionStorage.getItem(RETURN_PATH_KEY);
  // "//" starts another site's address, a browser reads a backslash as a slash ("/\evil.example"), and control
  // characters are stripped before it reads the address.
  // eslint-disable-next-line no-control-regex
  return path && path.startsWith("/") && !path.startsWith("//") && !/[\\\u0000-\u001f]/.test(path) ? path : null;
}

export function clearReturnPath(): void {
  sessionStorage.removeItem(RETURN_PATH_KEY);
}
